import React, { useEffect, useRef, useState } from 'react';
import { useSystem } from '@ohif/core';
import { Button, Tooltip, TooltipContent, TooltipTrigger, useImageViewer } from '@ohif/ui-next';
import { useDiagnosisStatus } from '../../hooks/useDiagnosisStatus';
import { DiagnosisState } from '../../utils';
import { AccountRoleEnum } from '../../services/SessionService';
import ButtonContent from './ButtonContent';
import AssessmentDialog from './AssessmentDialog';
import './DiagnosisButtonStyles.css';

const BASE_CLASS =
  'diagnosis-button flex h-10 min-w-[160px] items-center justify-center gap-2 px-4 font-medium transition-all duration-300';

/**
 * develop hard-appended this component inside extensions/default/src/Toolbar/Toolbar.tsx
 * (:83-90), outside the toolbar service entirely, reading the study UID from
 * `new URLSearchParams(location.search)` at :16. 3.13's Toolbar.tsx has diverged
 * structurally — it is button-agnostic and drives everything through useToolbar() — so
 * that file could not be cherry-picked even if the fork's structural rule allowed it.
 *
 * ONE PROP, `tooltip`. The toolbar spreads id, location, onInteraction, isOpen, isLocked,
 * viewportId and the definition's componentProps onto every button component
 * (Toolbar.tsx:80); this one reads only `tooltip`, sourced from
 * SYCAI_DIAGNOSIS_BUTTON.props in getCustomizationModule.ts.
 *
 *   - servicesManager comes from useSystem(), whose provider mounts above BrowserRouter.
 *   - the study UID comes from useImageViewer(), because Mode.tsx:461 wraps the whole
 *     layout — toolbar included — in ImageViewerProvider (plan D-1). Its context defaults
 *     to null, hence the optional chain.
 *
 * NO `evaluate` (D8). This is a custom uiType that re-renders from its own hook, so it
 * needs neither an evaluator nor registerEventForToolbarUpdate — and avoiding the latter
 * avoids a leak: its unsubscribe handles land in ToolbarService._serviceSubscriptions
 * (declared :113, pushed :169), which is push-only, never iterated and never drained,
 * so an extension calling it from onModeEnter accumulates a fresh set every mode entry.
 */
/**
 * ImageViewerProvider.tsx:8 creates its context with `createContext(null)`, so
 * useImageViewer()'s inferred result is `null` and every field read off it is an error.
 * The real shape is fixed at :12-14. Declared here rather than fixed at the provider
 * because that file is upstream and the fork does not modify platform/.
 */
interface ImageViewerContextValue {
  StudyInstanceUIDs: string[];
}

export default function DiagnosisButton({ tooltip }: { tooltip?: string }) {
  const { servicesManager } = useSystem();
  const { sessionService } = servicesManager.services;

  const imageViewer = useImageViewer() as ImageViewerContextValue | null;
  const studyInstanceUid = imageViewer?.StudyInstanceUIDs?.[0];

  /**
   * Disabled, not hidden — unlike OrganReviewButton.tsx's own D4 gate, confirmed with
   * the user 2026-08-19: the button stays visible for every role, folded into
   * `isDisabled` below, so a USER/no-role session still sees Diagnose exists and why
   * it is unavailable (the tooltip), rather than the control disappearing outright.
   *
   * The argument to useDiagnosisStatus is still gated, independent of that visibility
   * choice: passing `undefined` for a USER/no-role session keeps the effect that calls
   * `diagnosisService.track()` (useDiagnosisStatus.ts:105) from ever mounting, so it
   * never logs a 403 per study. Not a security boundary — the endpoint behind the same
   * session cookie is.
   *
   * REVIEWER and ENVIRONMENT_ADMIN both pass: diagnosisState.ts's own D4 already keeps
   * admin out of REQUIRES_ASSESSMENT (routes them to REVOCABLE instead, confirmed with
   * the user 2026-08-10), so this only has to add the role that D4 never covered —
   * plain USER, who currently falls through to REQUIRES_ASSESSMENT same as REVIEWER.
   * Excluding admin here too would also take away their existing revoke action.
   */
  const role = sessionService.getRole();
  const canAccessDiagnosis =
    role === AccountRoleEnum.REVIEWER || role === AccountRoleEnum.ENVIRONMENT_ADMIN;

  const { state, trigger, revoke } = useDiagnosisStatus(
    canAccessDiagnosis ? studyInstanceUid : undefined
  );
  const [assessmentOpen, setAssessmentOpen] = useState(false);

  /**
   * Where focus goes when the assessment dialog closes, because Radix cannot work it out:
   * its own onCloseAutoFocus focuses `context.triggerRef`
   * (@radix-ui/react-dialog/dist/index.mjs:146-148), and only `DialogTrigger` ever
   * populates that ref (:63). This dialog has no DialogTrigger — it opens from the click
   * below — so without this the reviewer is returned to `<body>`. `Button` is a
   * forwardRef and `TooltipTrigger asChild` composes through Slot
   * (@radix-ui/react-slot/dist/index.mjs:38), so the ref reaches the real <button>.
   */
  const buttonRef = useRef<HTMLButtonElement>(null);

  /**
   * `assessmentOpen` is this component's, but the dialog that reads it is mounted only
   * while the state is REQUIRES_ASSESSMENT *and* a study UID has resolved. Left set as
   * either drops — a poll tick landing PROCESSING, an admin's REVOCABLE, or the study
   * list emptying out from under it — the dialog unmounts with the flag still true and
   * re-opens unprompted the moment both conditions are true again.
   *
   * Reset, rather than deriving openness from the state: Save & Close leaves the study
   * in REQUIRES_ASSESSMENT, so a derived flag could never be closed at all.
   */
  useEffect(() => {
    if (state !== DiagnosisState.REQUIRES_ASSESSMENT || !studyInstanceUid) {
      setAssessmentOpen(false);
    }
  }, [state, studyInstanceUid]);

  /**
   * Exhaustive, like ButtonContent's CONTENT map. The three-key object literal this
   * replaces yielded `undefined` for PROCESSING and ASSESSED — the right behaviour,
   * unenforced, and indistinguishable from having forgotten them. A new DiagnosisState
   * member now fails to compile here until it is given a handler or an explicit
   * `undefined`.
   */
  const handlers: Record<DiagnosisState, (() => void) | undefined> = {
    [DiagnosisState.IDLE]: trigger,
    [DiagnosisState.PROCESSING]: undefined,
    [DiagnosisState.REQUIRES_ASSESSMENT]: () => setAssessmentOpen(true),
    [DiagnosisState.ASSESSED]: undefined,
    [DiagnosisState.REVOCABLE]: revoke,
  };

  const onClick = handlers[state];

  const isDisabled = !canAccessDiagnosis || !studyInstanceUid || !onClick;

  const className = [
    BASE_CLASS,
    state === DiagnosisState.PROCESSING ? 'in-progress' : '',
    state === DiagnosisState.ASSESSED ? 'assessed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {/*
        `aria-disabled`, not the native attribute, for the same reason
        ToggleGroup.tsx:77-85 gives: a native `<button disabled>` is unfocusable,
        so PROCESSING and ASSESSED would put the button — and the tooltip explaining
        why it is unavailable — out of keyboard reach. `aria-disabled:opacity-50`
        replaces `Button`'s `disabled:opacity-50`
        (platform/ui-next/src/components/Button/Button.tsx:8).

        NOTHING GUARDS THE CLICK, and that holds in all four disabling cases, not
        just the two obvious ones. PROCESSING and ASSESSED map to `undefined` in
        `handlers` above, so there is no handler to fire. The other two — no
        `studyInstanceUid`, and `!canAccessDiagnosis` — leave a real handler
        attached, and both are inert the same way: `useDiagnosisStatus` above is
        called with `canAccessDiagnosis ? studyInstanceUid : undefined`, so a role
        that fails the gate makes the HOOK see no uid regardless of whether the
        viewer has one, and `trigger`/`revoke` (useDiagnosisStatus.ts:119-129) are
        already wrapped in that same uid test. REQUIRES_ASSESSMENT's handler cannot
        be reached without a uid either, because the dialog below is gated on one too.
      */}
      {/*
        The trigger is the Button itself, not a <span>. Upstream's ToolButton.tsx:85-86
        wraps a <span> because "a disabled button does not fire events" -- but this button
        sets no native `disabled`, only `aria-disabled` (see the comment above), and
        Radix's TooltipTrigger opens on focus and puts aria-describedby on the trigger
        element, so a <span> here would leave keyboard users with no tooltip at all.
      */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={buttonRef}
            dataCY="sycai-diagnosis-button"
            className={className}
            aria-disabled={isDisabled || undefined}
            onClick={onClick}
          >
            <ButtonContent state={state} />
          </Button>
        </TooltipTrigger>
        {tooltip && <TooltipContent side="bottom">{tooltip}</TooltipContent>}
      </Tooltip>

      {state === DiagnosisState.REQUIRES_ASSESSMENT && studyInstanceUid && (
        <AssessmentDialog
          open={assessmentOpen}
          onOpenChange={setAssessmentOpen}
          studyInstanceUid={studyInstanceUid}
          returnFocusRef={buttonRef}
        />
      )}
    </>
  );
}
